import { StepBase } from './StepBase';
import { PageStepType } from './PageTypes';
import { StringOptions } from '../utils/StringOptions';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { DataQuery } from '../utils/DataQuery';
import { DataRecord } from '../utils/DataRecord';
import { DataServices } from '../utils/DataServices';
import { DataDictionary } from '../utils/DataDictionary';
import { DataDictionaryTable } from '../utils/DataDictionary';
import { DataFormatter } from '../dataformats/DataFormatter';
import { DataDictionaryTableRawType, DataDictionaryTableType } from '../types/DataDictionaryType';

import { SubEvent } from 'sub-events';
import { DataTools } from '../utils/DataTools';
import { DataSet } from '../utils/DataSet';
import { type } from 'os';

interface CellValue
{
	sortValue: any;
	text: string;
	link?: string;
	icon?: string;
	align: string;
	txtColor?: string;
	bgColor?: string;
}

interface callbackRow
{
	(record: DataRecord): Promise<any>;
}

export class StepTable extends StepBase
{
	// The actual data that will be used to output the table
	dataset: DataSet = new DataSet();

	// Reference to the logic engine
	logicRef: LogicEngine | undefined;

	// Callback function for each record to pre-process it before adding to the data set
	evRecordCallback: callbackRow | undefined;

	// The columns to show in the output
	columns: StepTable.Column[] = [];

	// Holds all dataset values after formatting for output
	formattedData: CellValue[][] | undefined;

	// Option to indicate that DataGrid should be used for this output
	_dataTable: boolean = false;

	// Optional settings from the "Option"
	listOptions: { [keyName: string]: string } = {};

	/** Get the type of table, can be Data (Grid) or Normal */
	getTableType(): 'Data' | 'Normal'
	{
		if (this._dataTable) return 'Data';
		return 'Normal';
	}

	/** Add a column to the list of tabs we're tracking in this holder */
	async addOption(step: PageStepType)
	{
		if (!this.logicRef)
			throw "Exception: should not happen but logicRef in StepTabs is null";

		if (!this.dataset.checkDatasetOption(step))
		{
			// Key is the configuration lookup key
			let key = step.step_option ? step.step_option : "";
			key = key.toLowerCase().trim();

			// Value from the text_en column
			let value = step.text_en ? this.logicRef.processTextReplacement(step.text_en) : (step.option_1 ? step.option_1 : "");
			this.listOptions[key] = value;
		}
	}

	/** Add a column to the list of tabs we're tracking in this holder */
	addColumn(step: PageStepType)
	{
		if (!this.logicRef) throw 'Exception: should not happen but logicRef in StepTabs is null';

		let tab: StepTable.Column = {
			text: this.logicRef.processTextReplacement(step.text_en ? step.text_en : ''),
			type: new StringOptions(step.step_option),
			is_action: false,
			link: '',
			format: 'Text',
			align: 'left',
			template: step.field_id ? step.field_id : '',
		};

		//  Check for duplicate column
		for (const col of this.columns)
		{
			if (col.text === tab.text) return;
		}

		if (this.options.checkOption('Counter'))
		{
			tab.align = 'right';
			tab.format = 'number';
		}

		let iconMatch = tab.text.match(/(<[^>]+>\s*)/);
		if (iconMatch)
		{
			tab.text = tab.text.replace(iconMatch[1], '');
			tab.icon = iconMatch[1].trim().replace('<', '').replace('>', '');
		}

		// GoToRoute in the step_option means we're clickable action column
		// the target route is in the option 1 column
		if (tab.type.checkOption('GoToRoute'))
		{
			tab.is_action = true;
			tab.link = step.option_1 ? step.option_1 : '';
		}

		// If a column type is defined in Option 2 set the display format
		if (step.option_2)
		{
			tab.format = step.option_2;
		}

		let df = new DataFormatter();
		tab.align = df.getAlign(tab.format);

		this.columns.push(tab);
	}

	async processEndTable(logic: LogicEngine)
	{
		this.formattedData = await this.generateFormattedData();
	}

	/**
	 *  Process the data type here.
	 *  Load the data related to the Query data type.
	 */
	async processStep(logic: LogicEngine): Promise<boolean>
	{
		this.logicRef = logic;
		this.dataset.logicRef = logic;

		await super.processStep(logic);

		// Indicates a data table
		if (this.options.checkOption('DataGrid') || this.options.checkOption("Grid"))
			this._dataTable = true;

		this.dataset.setOptions(this.option_1);

		if (this.options.checkOption('Counter'))
		{
			// Create a record based on start and end value
			this.dataset.generateDatasetFromCounter(this.text);
			return true;
		}

		if (this.options.checkOption('List'))
		{
			this.dataset.generateDatasetFromList(this.text);
			return true;
		}

		if (this.options.checkOption('Query'))
		{
			await this.dataset.generateDatsetFromQuery(this.text);
			return true;
		}

		if (this.options.checkOption('QueryGroup'))
		{
			await this.dataset.generateDatasetFromGroupBy(this.text, this.field_id);
			return true;
		}

		// TODO: Handle other types of data
		console.error('StepTable unknown table data source');
		return false;
	}

	getFormattedData(): CellValue[][]
	{
		if (this.formattedData) return this.formattedData;

		return [];
	}

	/** Loop through the dataset and format the values for output.
	If the callback is defined in evRecordCallback then it is called
	for each record.  The result is used.  Additional fields can be added or changed
	in the callback.
	*/
	async generateFormattedData(): Promise<CellValue[][]>
	{
		if (!this.logicRef) throw 'Missing LogicRef at StepTable getFormattedData()';

		this.dataset.logicRef = this.logicRef;
		let df = new DataFormatter();

		let results: CellValue[][] = [];
		for (const row of this.dataset.getRecords())
		{
			// For a cell to a specific color when set
			let forceTextColor: string | undefined = undefined;
			let forceBackColor: string | undefined = undefined;

			let rowData: CellValue[] = [];

			if (LogicEngine.debugOutput)
				console.log("StepTable Execute Row:", row.data);

			this.logicRef?.import(row.data);

			if (this.evRecordCallback)
			{
				let obj = await this.evRecordCallback(row);
				if (obj && typeof obj === 'object')
				{
					this.logicRef.import(obj);
				}
			}

			// Process any color change actions
			for (const action of this.dataset.actions)
			{
				if (action.type === "RowColor")
				{
					const conditionValue = this.logicRef.processTextReplacement(action.condition);
					// console.log("Process condition '", action.condition, "' =", conditionValue, "==>", this.logicRef.getVariable("counter"));
					if (conditionValue && conditionValue != "false" && conditionValue != "no" && conditionValue != "0")
					{
						forceBackColor = action.targetAmount;
					}
				}
			}

			for (const col of this.columns)
			{
				let cell: CellValue = {
					sortValue: '',
					text: this.logicRef.processTextReplacement(col.template),
					align: 'left',
				};

				// RowColor is forced
				if (forceBackColor) cell.bgColor = forceBackColor;

				// Check for forced column color
				// Process any color change actions
				for (const action of this.dataset.actions)
				{
					if (action.type === "ColumnColor" && action.targetVariable === col.text)
					{
						// const conditionValue = this.logicRef.processTextReplacement(action.condition);
						// if (conditionValue) cell.bgColor = action.targetAmount;
					}
				}

				if (col.format != 'Text')
				{
					cell.sortValue = df.unformatData(col.format, cell.text);
					cell.align = col.align;
				} else
				{
					cell.sortValue = cell.text;

					let iconMatch = cell.text.match(/(<[^>]+>\s*)/);
					if (iconMatch)
					{
						cell.text = cell.text.replace(iconMatch[1], '');
						cell.sortValue = cell.sortValue.replace(iconMatch[1], '');
						cell.icon = iconMatch[1].trim().replace('<', '').replace('>', '');
					}
				}

				if (col.is_action)
				{
					cell.link = this.logicRef.processTextReplacement(col.link);
				}

				rowData.push(cell);
			}
			results.push(rowData);
		}

		return results;
	}
}

export namespace StepTable
{
	export interface Column
	{
		text: string;
		type: StringOptions;
		template: string;
		is_action: boolean;
		link: string;
		format: string;
		align: string;
		icon?: string;
	}
}
